<template>
  <div class="external-invoice-standard">
    <v-form class="grid" @submit.prevent="saveForm" ref="form">
      <v-row>
        <v-col cols="3" class="label pb-8">
          {{ $t('period') }}
        </v-col>
        <v-col cols="3">
          <date-picker
              v-model="form.period"
              type="month"
              solo
              :rules="[rules.required]"
              :allowed-dates="allowedMonths"
          />
        </v-col>
        <v-col  cols="3"></v-col>
        <v-col  cols="3"></v-col>
      </v-row>

      <v-row class="with-field-controls">
        <v-col cols="3" class="label pb-8">
          {{ $t('register-external-operator.zeplug.off-peak-hour') }}
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.offPeak.consumption"
              type="number"
              min="0"
              max="2000"
              :rules="[rules.required, rules.max2000]"
              solo
              :suffix="$t('usages.kwh')"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.offPeak.amount"
              type="number"
              min="0"
              max="1000"
              :rules="[rules.required, rules.max1000]"
              solo
              :suffix="$t('register-external-operator.euro-exclude-vat')"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.offPeak.amountIncludingVat"
              type="number"
              min="0"
              max="1000"
              :rules="[rules.required, rules.max1000]"
              solo
              :suffix="$t('register-external-operator.euro-include-vat')"
          />
        </v-col>
      </v-row>
      <v-row class="with-field-controls">
        <v-col class="label pb-8">
          {{ $t('register-external-operator.zeplug.on-peak-hour') }}
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.peak.consumption"
              type="number"
              min="0"
              max="2000"
              :rules="[rules.max2000]"
              solo
              :suffix="$t('usages.kwh')"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.peak.amount"
              type="number"
              min="0"
              max="1000"
              :rules="[rules.max1000]"
              solo
              :suffix="$t('register-external-operator.euro-exclude-vat')"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.peak.amountIncludingVat"
              type="number"
              min="0"
              max="1000"
              :rules="[rules.max1000]"
              solo
              :suffix="$t('register-external-operator.euro-include-vat')"
          />
        </v-col>
      </v-row>
      <v-row class="with-field-controls">
        <v-col class="label pb-8">
          {{ $tc('subscription.subscription') }}
        </v-col>
        <v-col cols="3"></v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.subscription.amount"
              type="number"
              min="0"
              max="50"
              :rules="[rules.max50]"
              solo
              :suffix="$t('register-external-operator.euro-exclude-vat')"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
              v-model.number="form.data.subscription.amountIncludingVat"
              type="number"
              min="0"
              max="50"
              :rules="[rules.max50]"
              solo
              :suffix="$t('register-external-operator.euro-include-vat')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="label">
          {{ $t('total') }}
        </v-col>
        <v-col cols="3" class="justify-end">
          {{ total.consumption.toFixed(3) }} {{ $t('usages.kwh') }}
        </v-col>
        <v-col cols="3" class="justify-end">
          {{ total.amount.toFixed(2) }} {{ $t('register-external-operator.euro-exclude-vat') }}
        </v-col>
        <v-col cols="3" class="justify-end">
          {{ total.amountIncludingVat.toFixed(2) }} {{ $t('register-external-operator.euro-include-vat') }}
        </v-col>
      </v-row>
      <v-row class="mt-16 mb-6">
        <v-col class="justify-center flex-column">
          <div class="d-flex flex-wrap mb-6 justify-center" style="gap: 10px;">
            <v-chip small v-for="(file, i) in files"  :key="'file-' + i" @click="previewFile(file)"  color="gray" close @click:close="deleteFile(i)">
              {{ file.name }} ({{ (file.size/1000).toFixed(2) }} ko)
            </v-chip>
          </div>
          <template v-if="files.length < 5">
            <v-file-input ref="fileinput" v-model="addFiles" multiple v-show="false"  accept="image/*,application/pdf" />
            <v-btn  @click="uploadFile('fileinput')">{{ $t('register-external-operator.upload-invoice') }}</v-btn>
            <span class="caption grey--text">{{ $t('register-external-operator.upload-invoice-hint') }}</span>
          </template>
        </v-col>
      </v-row>
      <v-row >
        <v-col>
          <v-checkbox
              :label="$t('register-external-operator.declaration')"
              v-model="form.declaration"
          />
        </v-col>
      </v-row>
      <v-row class="py-6">
        <v-col class="justify-center">
          <v-btn color="primary" type="submit" :disabled="!canSave">{{ $t('register-external-operator.declare-consumption')}}</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="legend">
          {{ $t('register-external-operator.zeplug.legend') }}
        </v-col>
      </v-row>

    </v-form>
  </div>
</template>

<script>
import RegisterExternalInvoiceMixin from "@pages/MeterDevice/RegisterExternalInvoice/RegisterExternalInvoiceMixin";
import dayjs from "dayjs";

export default {
  data() {
    return {
      form: {
        period: dayjs().subtract(1, 'month').format('YYYY-MM'),
        data: {
          offPeak: {
            consumption: null,
            amount: null,
            amountIncludingVat: null,
          },
          peak: {
            consumption: null,
            amount: null,
            amountIncludingVat: null,
          },
          subscription: {
            amount: null,
            amountIncludingVat: null,
          },
        },
        declaration: false,
      },
    }
  },

  mixins: [
      RegisterExternalInvoiceMixin,
  ],

  mounted() {

  },

  watch: {
    'form.file'(val) {
      console.log(val)
    }
  },

  computed: {

    canSave() {
      return this.form.period && this.files.length > 0 && this.form.data.offPeak.consumption && this.form.data.offPeak.amount && this.form.data.offPeak.amountIncludingVat && this.form.declaration
    },

    isOffPeakKwhPriceValid() {
      return this.isKwhPriceValid(this.form.data.offPeak);
    },

    isPeakKwhPriceValid() {
      return this.isKwhPriceValid(this.form.data.peak);
    },

    isOffPeakAmountIncludingVatValid() {
      return this.isAmountIncludingVatValid(
          this.form.data.offPeak,
          this.energyVatRate
      );
    },

    isPeakAmountIncludingVatValid() {
      return this.isAmountIncludingVatValid(
          this.form.data.peak,
          this.energyVatRate
      );
    },

    isSubscriptionAmountIncludingVatValid() {
      return this.isAmountIncludingVatValid(
          this.form.data.subscription,
          this.subscriptionVatRate
      );
    },

    total() {
      return {
        consumption: this.form.data.offPeak.consumption + this.form.data.peak.consumption,
        amount: this.form.data.offPeak.amount + this.form.data.peak.amount + this.form.data.subscription.amount,
        amountIncludingVat: this.form.data.offPeak.amountIncludingVat + this.form.data.peak.amountIncludingVat + this.form.data.subscription.amountIncludingVat,
      }
    }
  },

  methods: {


    validate() {
      if(!this.isOffPeakKwhPriceValid) {
        this.$dialog.notify.warning(this.$t('register-external-operator.zeplug.off-peak-price-error'));
        return false
      }
      if(!this.isPeakKwhPriceValid) {
        this.$dialog.notify.warning(this.$t('register-external-operator.zeplug.on-peak-price-error'));
        return false
      }
      if(!this.isOffPeakAmountIncludingVatValid) {
        this.$dialog.notify.warning(this.$t('register-external-operator.zeplug.off-peak-total-error'));
        return false
      }
      if(!this.isPeakAmountIncludingVatValid) {
        this.$dialog.notify.warning(this.$t('register-external-operator.zeplug.on-peak-total-error'));
        return false
      }
      if(!this.isSubscriptionAmountIncludingVatValid) {
        this.$dialog.notify.warning(this.$t('register-external-operator.zeplug.subscription-total-error'));
        return false
      }
      return true
    }

  }
}
</script>

<style lang="scss">

</style>